import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { urls, currentLang } from "../../shared/seo/links";
import "./terms.css";

const CONTACT_EMAIL = "contact@avoconsulte.com";
const GOOGLE_ADS_URL = "https://policies.google.com/technologies/ads";

const SECTIONS = [
  { id: "logs", icon: "fas fa-clipboard-list", key: "logs" },
  { id: "cookies", icon: "fas fa-cookie-bite", key: "cookies" },
  { id: "third-party", icon: "fas fa-external-link-alt", key: "third", link: true },
  { id: "consulting", icon: "fas fa-user-shield", key: "consulting" },
  { id: "updates", icon: "fas fa-sync-alt", key: "updates" },
];

// Highlights the table-of-contents entry of the section being read.
function useActiveSection() {
  const [active, setActive] = useState(SECTIONS[0].id);
  useEffect(() => {
    if (!("IntersectionObserver" in window)) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting);
        if (visible.length) setActive(visible[0].target.id);
      },
      { rootMargin: "-20% 0px -65% 0px" }
    );
    SECTIONS.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);
  return [active, setActive];
}

export default function TermsContent() {
  const { t } = useTranslation();
  const [active, setActive] = useActiveSection();

  return (
    <>
      <section className="avc-page-hero">
        <div className="container avc-page-hero__inner">
          <span className="avc-eyebrow">
            <i className="fas fa-shield-alt" aria-hidden="true"></i>
            {t("privacy_eyebrow")}
          </span>
          <h1 className="avc-page-hero__title">{t("privacy_title")}</h1>
          <p className="avc-page-hero__lead">{t("privacy_lead")}</p>
        </div>
      </section>

      <div className="avc-page">
        <div className="container">
          <div className="avc-legal">
            <aside className="avc-legal__toc" aria-label={t("privacy_toc")}>
              <span className="avc-legal__toc-title">{t("privacy_toc")}</span>
              <ol className="avc-legal__toc-list">
                {SECTIONS.map((s, i) => (
                  <li key={s.id}>
                    <a
                      href={"#" + s.id}
                      className={active === s.id ? "is-active" : undefined}
                      aria-current={active === s.id ? "true" : undefined}
                      onClick={() => setActive(s.id)}
                    >
                      <span className="avc-legal__toc-num">{i + 1}</span>
                      {t("privacy_" + s.key + "_title")}
                    </a>
                  </li>
                ))}
              </ol>
            </aside>

            <div className="avc-legal__body">
              <p className="avc-legal__intro">{t("privacy_intro")}</p>

              {SECTIONS.map((s, i) => (
                <section key={s.id} id={s.id} className="avc-legal__card">
                  <span className="avc-legal__icon" aria-hidden="true">
                    <i className={s.icon}></i>
                  </span>
                  <div>
                    <h2 className="avc-legal__title">
                      <span className="avc-legal__num">{i + 1}</span>{" "}
                      {t("privacy_" + s.key + "_title")}
                    </h2>
                    <p className="avc-legal__text">{t("privacy_" + s.key + "_body")}</p>
                    {s.link && (
                      <a
                        className="avc-legal__link"
                        href={GOOGLE_ADS_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {t("privacy_third_link")}
                        <i className="fas fa-external-link-alt" aria-hidden="true"></i>
                      </a>
                    )}
                  </div>
                </section>
              ))}

              <div className="avc-legal__contact">
                <div>
                  <h2 className="avc-legal__contact-title">{t("privacy_contact_title")}</h2>
                  <p className="avc-legal__contact-text">{t("privacy_contact_text")}</p>
                  <a className="avc-legal__mail" href={"mailto:" + CONTACT_EMAIL}>
                    <i className="far fa-envelope" aria-hidden="true"></i>
                    {CONTACT_EMAIL}
                  </a>
                </div>
                <a className="avc-btn avc-btn--gold" href={urls.toHref(urls.sectionPath("contact", currentLang()))}>
                  {t("privacy_contact_btn")}
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
